import { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Sun, Moon, Sparkles, Camera } from "lucide-react";
import PlantHero from "@/components/PlantHero";
import {
  isotaneDoseForDay,
  entryCompletion,
  isSelfieDay,
  getDailyTip,
} from "@/lib/skincare";

function ChecklistRow({ item, checked, onToggle, testid, special }) {
  return (
    <motion.button
      data-testid={testid}
      onClick={onToggle}
      whileTap={{ scale: 0.97 }}
      className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-colors text-left ${
        checked
          ? "bg-[#E9F0E8] border-[#CDE0D1]"
          : "bg-white border-[#E9EFEA]"
      }`}
    >
      <motion.div
        animate={checked ? { scale: [1, 1.2, 1] } : { scale: 1 }}
        transition={{ duration: 0.3 }}
        className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 border-2 ${
          checked
            ? "bg-[#4A6B53] border-[#4A6B53]"
            : "bg-white border-[#CDE0D1]"
        }`}
      >
        {checked && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
      </motion.div>
      <div className="flex-1 min-w-0">
        <div
          className={`font-heading font-medium ${
            checked ? "text-[#6B7A70] line-through" : "text-[#2A332C]"
          }`}
        >
          {item.label}
          {special && (
            <span className="ml-2 text-xs px-2 py-0.5 bg-[#CC7A6B]/15 text-[#CC7A6B] rounded-full">
              {special}
            </span>
          )}
        </div>
        <div className="text-xs text-[#6B7A70] mt-0.5">{item.desc}</div>
      </div>
    </motion.button>
  );
}

export default function TodayView({
  entry,
  setEntry,
  dayNum,
  streak,
  stage,
  totalCompleted,
  startDate,
  morningItems,
  nightItems,
}) {
  const dose = isotaneDoseForDay(dayNum);
  const completion = useMemo(
    () => entryCompletion(entry, morningItems, nightItems),
    [entry, morningItems, nightItems]
  );
  const tip = useMemo(() => getDailyTip(dayNum), [dayNum]);

  const toggle = (section, id) => {
    const next = !entry[section]?.[id];
    setEntry(
      {
        ...entry,
        [section]: { ...entry[section], [id]: next },
      },
      { playSound: true, checked: next }
    );
  };

  if (dayNum < 1) {
    return (
      <div className="px-6 pt-12 text-center">
        <div className="font-heading text-2xl text-[#2A332C] mb-2">
          Journey starts soon
        </div>
        <p className="text-[#6B7A70]">
          Your 60 days begin on{" "}
          <span className="font-semibold text-[#4A6B53]">
            {new Date(startDate).toLocaleDateString(undefined, {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </span>
          . The plant is sleeping until then.
        </p>
      </div>
    );
  }

  if (dayNum > 60) {
    return (
      <div className="px-6 pt-12 text-center">
        <Sparkles className="w-12 h-12 mx-auto text-[#E6B97A] mb-3" strokeWidth={1.5} />
        <div className="font-heading text-2xl text-[#2A332C] mb-2">
          You finished your 60 days
        </div>
        <p className="text-[#6B7A70]">
          Your garden is in full bloom. Keep tending your skin daily — you have built the habit.
        </p>
      </div>
    );
  }

  return (
    <div>
      <PlantHero stage={stage} dayNum={dayNum} streak={streak} dose={dose} />

      {/* Progress ring */}
      <div className="px-6 -mt-1 mb-2">
        <div className="bg-white rounded-3xl p-4 border border-[#E9EFEA] flex items-center gap-4 shadow-[0_4px_20px_rgba(42,51,44,0.04)]">
          <div className="relative w-14 h-14">
            <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90">
              <circle cx="18" cy="18" r="15" fill="none" stroke="#E9EFEA" strokeWidth="3" />
              <motion.circle
                cx="18"
                cy="18"
                r="15"
                fill="none"
                stroke="#4A6B53"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 15}`}
                initial={{ strokeDashoffset: 2 * Math.PI * 15 }}
                animate={{ strokeDashoffset: 2 * Math.PI * 15 * (1 - completion) }}
                transition={{ type: "spring" }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center font-heading font-semibold text-sm text-[#2A332C]">
              {Math.round(completion * 100)}%
            </div>
          </div>
          <div>
            <div className="font-heading font-medium text-[#2A332C]">Today&rsquo;s routine</div>
            <div className="text-xs text-[#6B7A70] mt-0.5">
              {totalCompleted} days completed · {Math.max(0, 60 - dayNum)} to go
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 mt-4">
        <div className="flex items-center gap-2 mb-3">
          <Sun className="w-4 h-4 text-[#E6B97A]" strokeWidth={1.8} />
          <h3 className="font-heading text-base font-medium text-[#2A332C]">Morning</h3>
          <span className="text-[10px] text-[#6B7A70] ml-auto">today&rsquo;s ritual</span>
        </div>
        <div className="space-y-2" data-testid="morning-checklist">
          {morningItems.map((item) => (
            <ChecklistRow
              key={item.id}
              item={item}
              checked={!!entry?.morning?.[item.id]}
              onToggle={() => toggle("morning", item.id)}
              testid={`morning-${item.id}`}
            />
          ))}
        </div>
      </div>

      <div className="px-6 mt-6">
        <div className="flex items-center gap-2 mb-3">
          <Moon className="w-4 h-4 text-[#4A6B53]" strokeWidth={1.8} />
          <h3 className="font-heading text-base font-medium text-[#2A332C]">Night</h3>
          <span className="text-[10px] text-[#6B7A70] ml-auto">tonight&rsquo;s ritual</span>
        </div>
        <div className="space-y-2" data-testid="night-checklist">
          {nightItems.map((item) => (
            <ChecklistRow
              key={item.id}
              item={item}
              checked={!!entry?.night?.[item.id]}
              onToggle={() => toggle("night", item.id)}
              testid={`night-${item.id}`}
              special={item.id === "isotane" ? dose : null}
            />
          ))}
          {isSelfieDay(dayNum) && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 rounded-2xl border border-dashed border-[#CC7A6B]/50 bg-[#FADBD8]/30 flex items-center gap-3"
              data-testid="selfie-prompt"
            >
              <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0">
                <Camera className="w-5 h-5 text-[#CC7A6B]" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <div className="font-heading font-medium text-[#2A332C]">
                  Day {dayNum} — selfie checkpoint
                </div>
                <div className="text-xs text-[#6B7A70]">
                  Open the Selfies tab and capture today&rsquo;s photo.
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {completion === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mx-6 mt-6 p-4 rounded-3xl bg-gradient-to-br from-[#E9F0E8] to-[#FADBD8]/40 border border-[#CDE0D1] text-center"
            data-testid="day-complete-banner"
          >
            <Sparkles className="w-6 h-6 mx-auto text-[#4A6B53] mb-1" strokeWidth={1.5} />
            <div className="font-heading text-base font-semibold text-[#2A332C]">
              Day {dayNum} complete · +1 leaf
            </div>
            <div className="text-sm text-[#6B7A70] mt-0.5">
              Your plant grew a little today.
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Daily tip */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mx-6 mt-6 mb-2 p-4 rounded-2xl bg-white border border-[#E9EFEA]"
        data-testid="daily-tip"
      >
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#6B7A70] mb-2">Day {dayNum} tip</div>
        <div className="flex items-start gap-3">
          <span className="text-xl leading-none mt-0.5">{tip.emoji}</span>
          <p className="text-sm text-[#2A332C] leading-relaxed">{tip.tip}</p>
        </div>
      </motion.div>
    </div>
  );
}
